import { create, StateCreator } from 'zustand';
import { OrderTrackerOrderState, OrderTrackerTabData } from './OrderTrackerTypes';
import { OrderTrackerWidgetPayload } from '../OrderTrackerWidget';
import { WidgetSubStore } from '@features/common/store/StoreTypes';
import { WidgetPayload } from '@features/dashboard/types';
import { isEqual } from 'lodash';
import { OrderState, OrderType } from '@protos/v2/order';

export interface OrderTrackerStore extends WidgetSubStore {
  tabIndex: number;
  setTabIndex: (index: number) => void;

  tabs: OrderTrackerTabData[];

  updateTab: (tabIndex: number, update: Partial<OrderTrackerTabData>) => void;
  addTab: () => void;
  deleteTab: (tabIndex: number) => void;

  tradingAccounts: string[] | undefined;
  setTradingAccounts: (accounts: string[]) => void;
}

const getDefaultTab = (index: number): OrderTrackerTabData => {
  return {
    index,
    orderType: 'all',
    orderState: ['all'] as OrderTrackerOrderState[],
    productSymbol: 'all',
    selectedColumns: ['id', 'created_timestamp', 'symbol', 'exchange', 'amount', 'price', 'order_type', 'order_state', 'user.full_name'],
    showUserOrdersOnly: true,
    tradingAccount: 'all',
  };
};

export const getWorkingOrderTab = (index: number): OrderTrackerTabData => {
  return { ...getDefaultTab(index), orderType: OrderType.LIMIT, orderState: [OrderState.NEW, OrderState.PARTIALLY_FILLED] };
};

export const getLimitOrderTab = (index: number): OrderTrackerTabData => {
  return { ...getDefaultTab(index), orderState: ['all'], orderType: OrderType.LIMIT };
};

const OrderStateLabelMap = new Map<OrderTrackerOrderState, string>([[OrderState.PARTIALLY_FILLED, 'PARTIAL']]);

export const getDefaultTabName = (data: OrderTrackerTabData) => {
  const { orderType, orderState, productSymbol, tradingAccount } = data;

  const parts: string[] = [];

  parts.push(orderType.toUpperCase().replaceAll('_', ' ') + ' ORDERS');

  if (productSymbol && productSymbol !== 'all') {
    parts.push(productSymbol.toUpperCase());
  }

  const stateParts = orderState.map(s => OrderStateLabelMap.get(s) || s);
  if (stateParts.length === 1) {
    if (stateParts[0] !== 'all') parts.push(stateParts[0]);
  } else {
    parts.push(stateParts.join(', '));
  }

  if (tradingAccount !== 'all') {
    parts.push(tradingAccount);
  }

  const result = parts.join(' | ').toUpperCase();

  return result;
};

export const makeOrderTrackerStore: StateCreator<OrderTrackerStore> = (set, get) => {
  return {
    tabIndex: 0,
    setTabIndex: (tabIndex: number) => {
      set({ tabIndex });
    },

    tabs: [getDefaultTab(0)],

    updateTab: (tabIndex: number, update: Partial<OrderTrackerTabData>) => {
      const newTabs = [...get().tabs];
      const toUpdate = newTabs[tabIndex];
      if (!toUpdate) {
        return;
      }

      newTabs[tabIndex] = { ...toUpdate, ...update };
      set({ tabs: newTabs });
    },

    addTab: () => {
      const tabs = get().tabs;
      set({ tabs: [...tabs, getDefaultTab(tabs.length)], tabIndex: tabs.length });
    },

    deleteTab: (tabIndex: number) => {
      const { tabs: currentTabs, tabIndex: currentTabIndex } = get();

      const newTabs = currentTabs
        .filter((t, i) => i !== tabIndex)
        .map((t, i) => {
          return { ...t, tabIndex: i, index: i };
        });

      let newTabIndex = currentTabIndex;
      if (currentTabIndex > newTabs.length - 1) {
        newTabIndex--;
      }

      if (newTabs.length === 0) {
        set({ tabs: [getDefaultTab(0)], tabIndex: 0 });
      } else {
        set({ tabs: newTabs, tabIndex: newTabIndex });
      }
    },

    initFromPayload: (payload: WidgetPayload): boolean => {
      const typedPayload = payload as OrderTrackerWidgetPayload;

      const state = get();
      const payloadRelevantState: OrderTrackerWidgetPayload = { tabIndex: state.tabIndex, tabs: state.tabs };

      if (!payload || !typedPayload.tabs) {
        return false;
      }

      // No tabs in payload
      if (typedPayload.tabs.length === 0) {
        return false;
      }

      // No need to update as the contents are the same
      if (isEqual(payloadRelevantState, typedPayload)) {
        return false;
      }

      set(payload as Pick<OrderTrackerStore, 'tabs' | 'tabIndex'>);
      return get().tabs.length > 0;
    },

    getPayloadFromState: () => {
      const { tabIndex, tabs } = get();
      return { tabIndex, tabs };
    },

    // TODO: move this to the AppStore
    tradingAccounts: [],
    setTradingAccounts: (tradingAccounts: string[]) => {
      set({ tradingAccounts: tradingAccounts.sort() });
    },
  };
};

export const useOrderTrackerStore = create<OrderTrackerStore>(makeOrderTrackerStore);
